import React, { useState } from 'react'
import { Send, RotateCcw, CircleCheck, Plus, Trash2 } from 'lucide-react'
import { openRfq } from '../api'
import { OpenRfqDto } from '../api/schema'
import { locations } from '../constants/pops'


function daysFromNowISO(n: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

// Slots generated per hour
/* 
function generateSlots(startDate: string, endDate: string): number[] {
  const [sy, sm, sd] = startDate.split('-').map(Number)
  const [ey, em, ed] = endDate.split('-').map(Number)
  const startMs = Date.UTC(sy, sm - 1, sd, -8)
  const endMs   = Date.UTC(ey, em - 1, ed, -8)
  if (endMs <= startMs) return []
  const slots: number[] = []
  for (let ms = startMs; ms < endMs; ms += 3_600_000)
    slots.push(Math.floor(ms / 1000))
  return slots
}*/

// Slots generated per day
function generateSlots(protection_period_from: string, protection_period_to: string): number[] {
  const [sy, sm, sd] = protection_period_from.split('-').map(Number)
  const [ey, em, ed] = protection_period_to.split('-').map(Number)
  const startMs = Date.UTC(sy, sm - 1, sd)
  const endMs = protection_period_from === protection_period_to
    ? startMs + 86_400_000          // same day = 1 slot
    : Date.UTC(ey, em - 1, ed + 1) // different days = inclusive
  const slots: number[] = []
  for (let ms = startMs; ms < endMs; ms += 86_400_000)
    slots.push(Math.floor(ms / 1000))
  return slots
}



// ── Per-location entry ────────────────────────────────────────────────────────
interface LocationEntry {
  id: number
  asnId: number //change to city id
  dirty_bandwidth_gbps: number  // Gbps per location\
  clean_bandwidth_gbps: number   // Gbps required at this location

}

// ── Posted bundle record ──────────────────────────────────────────────────────
interface PostedBundle {
  id: number
  auctionId: number
  totalSlots: number
  locations: string[]
  dirty_bandwidth_gbps: number[]  // Gbps per location
  clean_bandwidth_gbps: number[]  // Gbps per location
  submittedAt: string
}

let rowCounter = 1

function makeRow(): LocationEntry {
  return {
    id: rowCounter++,
    asnId: locations[0].id,
    dirty_bandwidth_gbps: 300,
    clean_bandwidth_gbps: 100,
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export const BuyerOrderEntry: React.FC<{ onSubmitSuccess?: () => void }> = ({ onSubmitSuccess }) => {
  const minDate = daysFromNowISO(4)

  const [protection_period_from, setProtection_period_from] = useState(minDate)
  const [protection_period_to, setProtection_period_to] = useState(minDate)  // same date allowed
  const [entries, setEntries] = useState<LocationEntry[]>([makeRow()])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [posted, setPosted] = useState<PostedBundle[]>([])
  const [showConfirm, setShowConfirm] = useState(false)
  // const [nextId, setNextId] = useState(1)
  // const [startHour, setStartHour] = useState(currentHour)
  // const [endHour,   setEndHour]   = useState(Math.min(currentHour + 2, 23))

  // ── Row helpers ──────────────────────────────────────────────────────────────

  function addRow() {
    setEntries(prev => [...prev, makeRow()])
  }

  function removeRow(id: number) {
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  function updateRow(id: number, patch: Partial<LocationEntry>) {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e))
  }


  // ── Build ONE order for the entire bundle ─────────────────────────────────────
  //
  // submit_auction(asns_alternatives[][], timeslots[], limit_price)
  //
  // asns_alternatives: one row per location — [[asnId1], [asnId2], ...]
  //   Each inner array is one "alternative" (one potential winner).
  //   The contract iterates all alternatives × all timeslots when computing residuals.
  //
  // timeslots: UNION of all slot timestamps across all selected locations.
  //
  // limit_price: single bundle ceiling in USD for the total across all slots & locations.
  //
  // This single call produces ONE on-chain auction (one rfq_id) covering the whole bundle.

  // function buildOrder(): Order | null {
  //   const allAsnIds = [...new Set(entries.map(e => e.asnId))]
  //   const allTimeslots = generateSlots(protection_period_from, protection_period_to)
  //   if (allTimeslots.length === 0 || allAsnIds.length === 0) return null
  //   const clean_bandwidth_gbps = allAsnIds.map(id => entries.find(e => e.asnId === id)?.clean_bandwidth_gbps ?? 100)
  //   const dirty_bandwidth_gbps = allAsnIds.map(id => entries.find(e => e.asnId === id)?.dirty_bandwidth_gbps ?? 300)
  //   return { timeslots: allTimeslots, options: allAsnIds.map(id => [id]), clean_bandwidth_gbps, dirty_bandwidth_gbps }
  // }

  // ── Submit ───────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    setShowConfirm(false)
    setSubmitting(true)
    setError(null)
    try {
      const openRfqDto: OpenRfqDto = {
        from_date: protection_period_from,
        until_date: protection_period_to,
        locations: entries.map(e => {
          return {
            location_id: e.asnId,
            dirty_bandwidth_gbps: e.dirty_bandwidth_gbps,
            clean_bandwidth_gbps: e.clean_bandwidth_gbps,
          }
        })
      }
      await openRfq(openRfqDto)
      // const locationLabels = [...new Set(entries.map(e =>
      //   LOCATIONS.find(l => l.id === e.asnId)?.label ?? `PoP ${e.asnId}`
      // ))]
      // const record: PostedBundle = {
      //   id: nextId,
      //   auctionId: -1,
      //   totalSlots: order.timeslots.length,
      //   locations: locationLabels,
      //   clean_bandwidth_gbps: order.clean_bandwidth_gbps,
      //   dirty_bandwidth_gbps: order.dirty_bandwidth_gbps,
      //   submittedAt: new Date().toLocaleTimeString('en-SG', {
      //     hour: '2-digit', minute: '2-digit', second: '2-digit'
      //   }),
      // }
      // setPosted(prev => [record, ...prev])
      // setNextId(n => n + 1)
      resetForm()
      onSubmitSuccess?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed.')
    } finally {
      setSubmitting(false)
    }
  }

  function resetForm() {
    rowCounter = 1
    setEntries([makeRow()])
    setProtection_period_from(minDate)
    setProtection_period_to(minDate)
    setError(null)
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
        New RFQ
      </h2>

      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
        <div className="map_image-wrapper" style={{ position: 'relative', width: '50%', flexShrink: 0 }}>
          <img loading="lazy" src="https://cdn.prod.website-files.com/683547223a94c8ff5c6e9843/683547223a94c8ff5c6e9ae6_Asean.avif" alt="" className="image_wrapper-map" style={{ display: 'block', width: '100%', height: 'auto' }} />
          <img loading="lazy" src="https://cdn.prod.website-files.com/683547223a94c8ff5c6e9843/69324b2f80cdf2ad365254ed_b40c8e8f9762cb5334af5bad5fc09c0c_Map%202025%20Nov.svg" alt="" className="image_wrapper-map" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
        </div>

        <div style={{ display: 'flex', gap: '1rem', overflowY: 'auto' }}>
          {[locations.slice(0, Math.ceil(locations.length / 2)), locations.slice(Math.ceil(locations.length / 2))].map((half, i) => (
            <table key={i} style={{ borderCollapse: 'collapse', fontSize: '10px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.1rem 0.5rem', borderBottom: '1px solid var(--color-border, #E5E7EB)', color: 'var(--color-text-secondary, #6B7280)', fontWeight: 500 }}>City</th>
                  <th style={{ textAlign: 'left', padding: '0.1rem 0.5rem', borderBottom: '1px solid var(--color-border, #E5E7EB)', color: 'var(--color-text-secondary, #6B7280)', fontWeight: 500 }}>Country</th>
                </tr>
              </thead>
              <tbody>
                {half.map((loc, j) => (
                  <tr key={loc.id} style={{ background: j % 2 === 1 ? 'rgba(0, 0, 0, 0.03)' : 'transparent' }}>
                    <td style={{ padding: '0.1rem 0.5rem', borderBottom: '1px solid var(--color-border, #F3F4F6)', color: 'var(--color-text-primary)' }}>{loc.city}</td>
                    <td style={{ padding: '0.1rem 0.5rem', borderBottom: '1px solid var(--color-border, #F3F4F6)', color: 'var(--color-text-primary)' }}>{loc.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </div>

      {/* ── Posted bundle history ── */}
      {posted.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {posted.map(rec => (
            <div key={rec.id} style={{
              borderRadius: '0.5rem', padding: '0.75rem 1rem',
              background: '#F0FDF4', border: '1px solid #BBF7D0',
              display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <CircleCheck size={16} color="#16A34A" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#15803D' }}>
                  RFQ opened — {rec.totalSlots} slot{rec.totalSlots !== 1 ? 's' : ''} across {rec.locations.join(', ')}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#4ADE80', marginTop: '0.1rem' }}>
                  {rec.locations.map((l, i) => `${l}: ${rec.dirty_bandwidth_gbps[i] ?? 100} Gb/s dirty · ${rec.clean_bandwidth_gbps[i] ?? 100} Gb/s clean`).join(' | ')}
                  &nbsp;&middot;&nbsp; at {rec.submittedAt}
                </div>
              </div>
              <button
                onClick={() => setPosted(prev => prev.filter(r => r.id !== rec.id))}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#86EFAC', fontSize: '1rem', lineHeight: 1, padding: '0 0.25rem'
                }}
                title="Dismiss"
              >&times;</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{
          borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.875rem',
          background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA'
        }}>
          {error}
        </div>
      )}

      {/* ── Bundle limit price ── */}
      <div style={card}>

        <div>
          <label style={labelStyle}>Protection Period</label>
          <p style={hintStyle}>
            Date range for the requested protection. All dates are in SGT (UTC+8). To ensure enough time for provisioning, protection period must start at least 4 days in the future.
          </p>
        </div>

        {/* Date/time row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', margin: '1rem 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <span style={colLabel}>Start (Min. 4 days in advance)</span>
            <input type="date" value={protection_period_from} min={minDate}   // was min={today}
              onChange={e => {
                const v = e.target.value
                setProtection_period_from(v)
                if (v > protection_period_to) setProtection_period_to(v)
              }}
              style={dateInputStyle} />

          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <span style={colLabel}>End (inclusive)</span>
            <input type="date" value={protection_period_to} min={protection_period_from}   // same as before
              onChange={e => setProtection_period_to(e.target.value)}
              style={dateInputStyle} />

          </div>
        </div>

        {/* Slot count feedback */}
        <div style={{ fontSize: '0.75rem', marginBottom: '1rem' }}>
          {(() => {
            const rangeOk = protection_period_to >= protection_period_from
            const count = rangeOk ? generateSlots(protection_period_from, protection_period_to).length : 0
            return rangeOk && count > 0 ? (
              <span style={{ color: '#0F766E' }}>
                {count} day{count !== 1 ? 's' : ''} · {protection_period_from === protection_period_to
                  ? `All day SGT on ${protection_period_from}`
                  : `${protection_period_from} → ${protection_period_to} SGT`}
              </span>
            ) : (
              <span style={{ color: '#EF4444' }}>Select a valid date range</span>
            )
          })()}
        </div>

      </div>


      {/* ── Location rows ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <div>
            <label style={labelStyle}>Scrubbing Locations</label>
            <p style={hintStyle}>Request protection for the following locations.</p>
          </div>
          <button onClick={addRow} style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
            border: '1px solid #0D8F7A', background: '#F0FDFA',
            color: '#0F766E', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}>
            <Plus size={13} /> Add Location
          </button>
        </div>

        {/* Column headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '400px auto auto auto',
          gap: '1.75rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #E2E8F0',
        }}>
          {['Location', 'Dirty bandwidth (Gb/s)', 'Clean bandwidth (Gb/s)', ''].map((h, i) => (
            <span key={i} style={{
              fontSize: '0.75rem', fontWeight: 600, color: '#64748B',
              ...(i === 3 ? { width: '1.5rem' } : {}),
            }}>{h}</span>
          ))}
        </div>

        {entries.map((entry) => (
          <LocationRow
            key={entry.id}
            entry={entry}
            canRemove={entries.length > 1}
            onChange={patch => updateRow(entry.id, patch)}
            onRemove={() => removeRow(entry.id)}
          />
        ))}
      </div>



      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={() => setShowConfirm(true)}
          disabled={submitting}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.625rem 1.25rem',
            background: submitting ? '#94A3B8' : '#0D8F7A',
            color: '#fff', fontSize: '0.875rem', fontWeight: 600,
            borderRadius: '0.5rem', border: 'none',
            cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
          }}>
          <Send size={15} />
          {submitting ? 'Opening RFQ…' : 'Submit RFQ'}
        </button>
        <button onClick={resetForm} style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.625rem 1rem', background: '#fff', color: '#475569',
          fontSize: '0.875rem', fontWeight: 600,
          borderRadius: '0.5rem', border: '1px solid #CBD5E1', cursor: 'pointer',
        }}>
          <RotateCcw size={15} /> Reset
        </button>
      </div>

      {/* ── Confirmation modal ── */}
      {showConfirm && (
        <div
          onClick={() => setShowConfirm(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '0.75rem',
              border: '1px solid #E2E8F0', padding: '1.5rem',
              width: '100%', maxWidth: '30rem',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
              display: 'flex', flexDirection: 'column', gap: '1rem',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#0F172A' }}>
                Confirm RFQ submission
              </h3>
              <p style={{ ...hintStyle, marginTop: '0.25rem' }}>
                This opens a Request for Quote with the specified demand. Please review before confirming.
              </p>
            </div>

            <div style={{ fontSize: '0.8125rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div>
                <span style={{ fontWeight: 600 }}>Protection period: </span>
                {protection_period_from === protection_period_to
                  ? `All day SGT on ${protection_period_from}`
                  : `${protection_period_from} → ${protection_period_to} SGT`}
              </div>
              <div>
                <span style={{ fontWeight: 600 }}>Locations ({entries.length}):</span>
                <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                  {entries.map(e => {
                    const loc = locations.find(l => l.id === e.asnId)
                    return (
                      <li key={e.id}>
                        {loc ? `${loc.city}, ${loc.country}` : `PoP ${e.asnId}`} — {e.dirty_bandwidth_gbps} Gb/s dirty, {e.clean_bandwidth_gbps} Gb/s clean
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConfirm(false)} style={{
                padding: '0.5rem 1rem', background: '#fff', color: '#475569',
                fontSize: '0.875rem', fontWeight: 600,
                borderRadius: '0.5rem', border: '1px solid #CBD5E1', cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.5rem 1.25rem',
                background: submitting ? '#94A3B8' : '#0D8F7A',
                color: '#fff', fontSize: '0.875rem', fontWeight: 600,
                borderRadius: '0.5rem', border: 'none',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}>
                <Send size={15} />
                {submitting ? 'Opening RFQ…' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// ── LocationRow sub-component ─────────────────────────────────────────────────

interface LocationRowProps {
  entry: LocationEntry
  canRemove: boolean
  onChange: (patch: Partial<LocationEntry>) => void
  onRemove: () => void
}

function LocationRow({ entry, canRemove, onChange, onRemove }: LocationRowProps) {


  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '400px auto auto auto',
      alignItems: 'center',
      gap: '1.75rem',
      padding: '0.75rem 0',
      borderBottom: '1px solid #F1F5F9',
    }}>
      {/* Location dropdown */}
      <select
        value={entry.asnId}
        onChange={e => onChange({ asnId: Number(e.target.value) })}
        style={{
          border: '1px solid #CBD5E1', borderRadius: '0.5rem',
          padding: '0.5rem 0.75rem', fontSize: '0.875rem',
          color: '#1E293B', background: '#fff', outline: 'none',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        {locations.map(loc => (
          <option key={loc.id} value={loc.id}>{loc.city}, {loc.country}</option>
        ))}
      </select>

      {/* Attack surface */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <input
          type="number" min={1} value={entry.dirty_bandwidth_gbps}
          onChange={e => onChange({ dirty_bandwidth_gbps: Math.max(1, Number(e.target.value)) })}
          style={numInputStyle}
        />
      </div>

      {/* Clean bandwidth */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <input
          type="number" min={1} value={entry.clean_bandwidth_gbps}
          onChange={e => onChange({ clean_bandwidth_gbps: Math.max(1, Number(e.target.value)) })}
          style={numInputStyle}
        />
      </div>

      {/* Remove */}
      <div style={{ width: '1.5rem' }}>
        {canRemove && (
          <button onClick={onRemove} title="Remove location" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#CBD5E1', padding: '0.25rem',
            display: 'flex', alignItems: 'center',
          }}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: '#fff', borderRadius: '0.75rem',
  border: '1px solid #E2E8F0', padding: '1.25rem',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.875rem', fontWeight: 600,
  color: '#334155', marginBottom: '0.25rem',
}

const numInputStyle: React.CSSProperties = {
  width: '7.5rem', border: '1px solid #CBD5E1', borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem', fontSize: '0.875rem',
  color: '#1E293B', outline: 'none', textAlign: 'right',
}

const hintStyle: React.CSSProperties = { fontSize: '0.75rem', color: '#94A3B8', lineHeight: 1.5 }

const dateInputStyle: React.CSSProperties = {
  width: '100%', border: '1px solid #CBD5E1', borderRadius: '0.375rem',
  padding: '0.375rem 0.75rem', fontSize: '0.875rem', color: '#1E293B',
  outline: 'none', boxSizing: 'border-box',
}
const colLabel: React.CSSProperties = {
  fontSize: '0.75rem', fontWeight: 600, color: '#64748B',
  textTransform: 'uppercase', letterSpacing: '0.05em',
}
